/**
 * useAdvancedMode Hook
 *
 * Manages the "advanced=true" query parameter for enabling advanced features.
 * The advanced mode is hidden by default and only enabled via URL query param.
 *
 * Features:
 * - Reads `advanced=true` from URL search params
 * - Provides helper to preserve the param when navigating
 * - Does not add the param to URL if not already present
 */

import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

/**
 * Query parameter key for advanced mode
 */
const ADVANCED_PARAM_KEY = 'advanced';

/**
 * Return type for the useAdvancedMode hook
 */
export interface UseAdvancedModeResult {
  /** Whether advanced mode is enabled (advanced=true in URL) */
  readonly isAdvancedMode: boolean;
  /** Get search params string to append to navigation URLs (includes leading '?' if params exist) */
  readonly getSearchString: () => string;
  /** Get the current search params to spread into Link/navigate */
  readonly searchParams: URLSearchParams;
}

/**
 * Hook to manage advanced mode via URL query parameter.
 *
 * @returns Object with isAdvancedMode flag and helpers for preserving params
 *
 * @example
 * ```tsx
 * const { isAdvancedMode, getSearchString } = useAdvancedMode();
 *
 * // Conditionally render advanced section
 * {isAdvancedMode && <AdvancedOptionsSection />}
 *
 * // Preserve param in navigation
 * <Link to={`/site/${siteId}${getSearchString()}`}>Go to site</Link>
 * ```
 */
export function useAdvancedMode(): UseAdvancedModeResult {
  const [searchParams] = useSearchParams();

  const isAdvancedMode = useMemo(() => {
    return searchParams.get(ADVANCED_PARAM_KEY) === 'true';
  }, [searchParams]);

  const getSearchString = useCallback(() => {
    const paramString = searchParams.toString();
    return paramString ? `?${paramString}` : '';
  }, [searchParams]);

  return {
    isAdvancedMode,
    getSearchString,
    searchParams,
  };
}
